/**
 * Mensajes de error de autenticación.
 *
 * Traduce los códigos de negocio de EDSuite API (`code`) a textos claros para el
 * usuario. Mantener el mapeo aquí evita mensajes duplicados y desalineados entre
 * formularios.
 */

import { isApiError } from '@/lib/http/api-error';

const MESSAGES: Record<string, string> = {
  // Credenciales
  IDENTITY_REQUIRED: 'Ingresa tu correo o código de acceso.',
  PASSWORD_REQUIRED: 'Ingresa tu contraseña.',
  INVALID_CREDENTIALS: 'Las credenciales no son correctas. Verifica los datos e inténtalo otra vez.',

  // Reglas de acceso por tipo de usuario
  NOT_ADMIN: 'Esta cuenta no tiene permisos de administrador. Usa la pestaña Operador.',
  IS_ADMIN: 'Los administradores deben ingresar con correo y contraseña.',

  // Estado de la cuenta
  USER_NOT_FOUND: 'No encontramos una cuenta con ese código de acceso.',
  USER_INACTIVE: 'Tu cuenta está desactivada. Contacta al administrador del sistema.',
  USER_UNAVAILABLE: 'La cuenta ya no está disponible. Inicia sesión nuevamente.',

  // Reconocimiento facial
  BIOMETRIA_SIN_COINCIDENCIA:
    'No reconocemos tu cara. Escribe tu contraseña o pide que registren tu cara en Usuarios.',
  BIOMETRIA_DUDOSA:
    'No pudimos distinguir tu cara con seguridad. Escribe tu contraseña.',
  BIOMETRIA_INVALIDA: 'La captura de la cara no era válida. Vuelve a intentarlo.',
  BIOMETRIA_LLENA:
    'Ya tienes el máximo de capturas registradas. Pide que borren alguna para registrar otra.',
  BIOMETRIA_SIN_PERMISO: 'No tienes permisos para gestionar la biometría de otra persona.',

  // Infraestructura
  TOO_MANY_REQUESTS: 'Demasiados intentos fallidos. Espera unos minutos antes de reintentar.',
  TIMEOUT: 'El servidor tardó demasiado en responder. Intenta nuevamente.',
  NETWORK_ERROR: 'No pudimos conectar con el servidor. Verifica tu conexión e inténtalo otra vez.',
  INTERNAL_ERROR: 'Ocurrió un error inesperado. Si persiste, contacta a soporte.',
  NON_JSON_RESPONSE: 'El servidor devolvió una respuesta inesperada. Verifica la URL de la API.',
  INVALID_JSON: 'El servidor devolvió una respuesta inesperada. Verifica la URL de la API.',
};

export function resolveAuthErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const mapped = MESSAGES[error.code];
    if (mapped) return mapped;

    if (error.isRateLimited) return MESSAGES.TOO_MANY_REQUESTS ?? error.message;
    if (error.status >= 500) return MESSAGES.INTERNAL_ERROR ?? error.message;

    return error.message;
  }

  if (error instanceof Error) return error.message;

  return 'Ocurrió un error inesperado al iniciar sesión.';
}
